import React, { useState, useRef, useEffect } from 'react';
import html2pdf from 'html2pdf.js';
import { useLocation } from 'react-router-dom';
import { getHsnCodeById } from '../../../services';

const Invoice = () => {
  const invoiceRef = useRef();
  const location = useLocation();
  const orderData = location?.state;

  // State for HSN code data
  const [hsn, setHsn] = useState({});

  const fetchHsnCode = async () => {
    try {
      const hsnId = orderData?.product_detail?.others?.hsn_code;
      if (!hsnId) return;

      const response = await getHsnCodeById(hsnId);
      setHsn(response); // { id, code, percent }
    } catch (error) {
      console.error('Error fetching HSN code:', error);
    }
  };

  useEffect(() => {
    fetchHsnCode();
  }, []);

  const formatDate = (isoDate) => {
    if (!isoDate) return new Date().toISOString().split('T')[0];
    return new Date(isoDate).toISOString().split('T')[0];
  };

  const [invoiceData] = useState({
    invoiceNo: orderData.invoice_no || 'MMQ2025-11001',
    invoiceDate: formatDate(new Date()),
    dueDate: formatDate(orderData?.created_date),
    poNo: orderData?.uid?.substring(0, 10) || 'SO-000000',
    billTo: {
      name: `${orderData?.buyer_firstname || ''} ${orderData?.buyer_lastname || ''}`.trim(),
      address: `${orderData?.delivery_address?.address || ''}\n${orderData?.delivery_address?.location || ''}, ${orderData?.delivery_address?.city || ''}\n${orderData?.delivery_address?.state || ''} - ${orderData?.delivery_address?.zip_code || ''}`,
      gstin: 'N/A',
      phone: orderData?.delivery_address?.mobile || ''
    },
    seller: {
      name: `${orderData?.seller_firstname || ''} ${orderData?.seller_lastname || ''}`.trim(),
      address: orderData?.product_detail?.address || '',
      city: orderData?.product_detail?.shipping_form || ''
    }
  });

  const calculateTotals = () => {
    const subTotal = parseFloat(orderData?.sub_total || 0);
    
    // Use hsn.percent from API response (e.g., 2.00%)
    const gstPercent = parseFloat(hsn?.percent || 0); // This is the total GST percentage
    
    // Calculate GST amount
    const gstAmount = (subTotal * gstPercent) / 100;
    
    // Split GST equally between CGST and SGST
    const totalCGST = gstAmount / 2;
    const totalSGST = gstAmount / 2;
    
    const totalAmount = subTotal + gstAmount;
    const balanceDue = parseFloat(orderData?.remain_amount || 0);

    return { 
      subTotal, 
      totalCGST, 
      totalSGST, 
      totalAmount, 
      gstPercent,  // Store this for display
      balanceDue,
      gstAmount   // Add this for total GST display
    };
  };

  const totals = calculateTotals();
  const paymentMade = parseFloat(orderData?.total || 0) - parseFloat(orderData?.remain_amount || 0);

  // Calculate individual GST rates for display
  const cgstRate = totals.gstPercent / 2;
  const sgstRate = totals.gstPercent / 2;

  // Enhanced numberToWords function that handles decimals correctly
  const numberToWords = (num) => {
    if (num === 0) return 'Zero Rupees Only';

    // Separate rupees and paise
    const rupees = Math.floor(num);
    const paise = Math.round((num - rupees) * 100);

    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    const convertLessThanOneThousand = (n) => {
      if (n === 0) return '';

      let result = '';

      if (n >= 100) {
        result += ones[Math.floor(n / 100)] + ' Hundred ';
        n %= 100;
      }

      if (n >= 20) {
        result += tens[Math.floor(n / 10)] + ' ';
        n %= 10;
      }

      if (n >= 10) {
        result += teens[n - 10] + ' ';
        n = 0;
      }

      if (n > 0) {
        result += ones[n] + ' ';
      }

      return result.trim();
    };

    const convertToWords = (n) => {
      if (n === 0) return 'Zero';

      let words = '';

      if (n >= 10000000) {
        words += convertLessThanOneThousand(Math.floor(n / 10000000)) + ' Crore ';
        n %= 10000000;
      }

      if (n >= 100000) {
        words += convertLessThanOneThousand(Math.floor(n / 100000)) + ' Lakh ';
        n %= 100000;
      }

      if (n >= 1000) {
        words += convertLessThanOneThousand(Math.floor(n / 1000)) + ' Thousand ';
        n %= 1000;
      }

      words += convertLessThanOneThousand(n);

      return words.trim();
    };

    let rupeesWords = convertToWords(rupees);
    let paiseWords = convertToWords(paise);

    let result = '';

    if (rupeesWords && rupeesWords !== 'Zero') {
      result += `${rupeesWords} Rupee${rupees === 1 ? '' : 's'}`;
    }

    if (paiseWords && paiseWords !== 'Zero') {
      if (result) result += ' and ';
      result += `${paiseWords} Paise`;
    }

    if (!result) result = 'Zero';

    return result + ' Only';
  };

  // Optimized PDF download for single page
  const downloadPDF = () => {
    const element = invoiceRef.current;

    // Hide the print buttons
    const printButtons = document.querySelectorAll('.print-hidden');
    printButtons.forEach(btn => btn.style.display = 'none');

    // Optimize element for PDF
    element.style.width = '210mm';
    element.style.height = 'auto';
    element.style.padding = '10mm';
    element.style.boxSizing = 'border-box';

    const opt = {
      margin: [5, 5, 5, 5],
      filename: `${invoiceData.invoiceNo}.pdf`,
      image: {
        type: 'jpeg',
        quality: 0.98,
      },
      html2canvas: {
        scale: 2,
        useCORS: true,
        letterRendering: true,
        scrollY: 0,
        width: 794, // A4 width at 96 DPI
        height: element.scrollHeight + 50,
        windowWidth: 794,
      },
      jsPDF: {
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true,
      }
    };

    html2pdf().set(opt).from(element).save().then(() => {
      // Restore element styles
      element.style.width = '';
      element.style.height = '';
      element.style.padding = '';
      printButtons.forEach(btn => btn.style.display = '');
    });
  };

  // Handle print
  const handlePrint = () => {
    window.print();
  };

  const getPaymentStatus = (status) => {
    const statusMap = {
      1: 'Pending',
      2: 'Partially Paid',
      3: 'Completed',
      4: 'Failed'
    };
    return statusMap[status] || 'Unknown';
  };

  const getOrderStatus = (status) => {
    const statusMap = {
      1: 'Pending',
      2: 'Confirmed',
      3: 'Processing',
      4: 'Shipped',
      5: 'Delivered',
      6: 'Cancelled'
    };
    return statusMap[status] || 'Unknown';
  };

  // Backend payment_type choices (1-10) — see order-management-implementation-plan-updated.md
  const getPaymentType = (type) => {
    const statusMap = {
      1: 'Cash',
      2: 'Bank Transfer',
      3: 'Credit Card',
      4: 'Debit Card',
      5: 'UPI / QR Code',
      6: 'Wallet',
      7: 'Cheque',
      8: 'Net Banking',
      9: 'Cash on Delivery',
      10: 'Other'
    };
    return statusMap[Number(type)] || 'Unknown';
  };

  const paymentTypeCode = Number(orderData?.payment_type);
  const isCod = paymentTypeCode === 9;
  const isQr = paymentTypeCode === 5;

  // Company contact / account details shown on the invoice depending on payment method
  const companyContact = (
    <>
      <p className="font-semibold">EquipMedy</p>
      <p>Email: info@equipmedy.in</p>
      <p>Mobile: +91-8595903932</p>
      <p>GSTIN: 07ACCFA9252A1ZS</p>
    </>
  );

  const companyBank = (
    <>
      <p className="font-semibold">IDFC FIRST Bank</p>
      <p>Account: 10155626447</p>
      <p>Name: AA MEDIE QUIP LLP</p>
      <p>IFSC: IDFB0021003</p>
      <p>Branch: Gurgaon Sector-14</p>
    </>
  );

  // Add CSS for print styles
  const printStyles = `
    @media print {
      .print-hidden {
        display: none !important;
      }
    }
  `;

  return (
    <div className="min-h-screen bg-gray-100 py-4 px-2">
      <style>{printStyles}</style>

      <div className="max-w-3xl mx-auto bg-white shadow-xl border border-gray-300">

        {/* Action Buttons */}
        <div className="flex justify-between items-center p-3 bg-gray-50 border-b print:hidden">
          <div className="flex gap-2">
            <button onClick={downloadPDF} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded print-hidden">
              Download PDF
            </button>
            <button onClick={handlePrint} className="px-3 py-1.5 bg-[#019c89] text-white text-sm rounded print-hidden">
              Print
            </button>
          </div>

          <div className="text-sm print-hidden">
            <span className="font-semibold mr-2">Status:</span>
            <span className={`px-2 py-1 rounded ${orderData?.order_status === 5 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
              {getOrderStatus(orderData?.order_status)}
            </span>
          </div>
        </div>

        {/* INVOICE CONTENT */}
        <div ref={invoiceRef} className="p-4 text-[12px]">

          {/* TOP HEADER — EXACT PDF STYLE */}
          <div className="grid grid-cols-3 border border-gray-300">

            {/* Left Box with Logo + Details */}
            <div className="col-span-2 border-r border-gray-300 p-3">
              <img src={process.env.PUBLIC_URL + "/EquipR.png"} alt="EquipMedy Logo" className="h-12 w-[300px] mb-1 object-contain" />
              <div className="leading-4 text-[11px]">
                <p className="font-semibold text-blue-800">EquipMedy</p>
                <p>B-99, 5th Floor, Phase-I Panchsheel Park, Malviya Nagar</p>
                <p>Near Triveni Complex, New Delhi - 110017</p>
                <p>Corporate Address: F-300, 2nd Floor, Sector 63, Noida, UP-201301</p>
                <p className="mt-1">Email: info@equipmedy.in | Mobile: +91-8595903932</p>
                <p className="font-semibold mt-1">GSTIN: 07ACCFA9252A1ZS</p>
              </div>
            </div>

            {/* Right — TAX INVOICE */}
            <div className="p-3 flex flex-col justify-center text-right">
              <p className="text-xl font-bold tracking-wide">PAYMENT INVOICE</p>

              <table className="text-[11px] w-full mt-2">
                <tbody>
                  <tr>
                    <td className="font-semibold">Invoice No:</td>
                    <td>{invoiceData.invoiceNo}</td>
                  </tr>
                  <tr>
                    <td className="font-semibold">Date:</td>
                    <td>{invoiceData.invoiceDate}</td>
                  </tr>
                  <tr>
                    <td className="font-semibold">Terms:</td>
                    <td>Advance</td>
                  </tr>
                  <tr>
                    <td className="font-semibold">P.O.#</td>
                    <td>{invoiceData.poNo}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* BILL TO SECTION */}
          <div className="border border-t-0 border-gray-300 p-3">
            <p className="font-bold mb-1">Bill To</p>

            <p className="font-semibold">{invoiceData.billTo.name}</p>
            <pre className="whitespace-pre-line text-[11px] text-gray-700">{invoiceData.billTo.address}</pre>

            <p className="mt-1 text-[11px]"><span className="font-semibold">Phone:</span> {invoiceData.billTo.phone}</p>
            <p className="text-[11px]"><span className="font-semibold">GSTIN:</span> {invoiceData.billTo.gstin}</p>
          </div>

          {/* PRODUCT TABLE - EXACT PDF STRUCTURE */}
          <table className="w-full mt-3 border border-gray-300 text-[11px]">
            <thead className="bg-gray-100">
              <tr>
                <th className="border p-1 w-8">#</th>
                <th className="border p-1 text-left">Item & Description</th>
                <th className="border p-1 w-20">HSN/SAC</th>
                <th className="border p-1 w-10">Qty</th>
                <th className="border p-1 w-20">Rate</th>
                <th className="border p-1 w-14">CGST %</th>
                <th className="border p-1 w-14">SGST %</th>
                <th className="border p-1 w-24">Amount</th>
              </tr>
            </thead>

            <tbody>
              <tr>
                <td className="border p-1 text-center">1</td>

                <td className="border p-1">
                  <p className="font-semibold">{orderData?.product_detail?.equip_name}</p>
                  <p className="text-[10px] text-gray-500">
                    Brand: {orderData?.product_detail?.brand} | Model: {orderData?.product_detail?.model}
                  </p>
                </td>

                <td className="border p-1 text-center">
                  {hsn?.code || '—'} <br/>
                  {hsn?.percent ? `${hsn.percent}%` : '—'}
                </td>

                <td className="border p-1 text-center">1</td>
                <td className="border p-1 text-right">₹ {parseFloat(orderData?.asking_price).toFixed(2)}</td>
                <td className="border p-1 text-center">
                  {cgstRate.toFixed(2)}%<br />₹ {totals.totalCGST.toFixed(2)}
                </td>
                <td className="border p-1 text-center">
                  {sgstRate.toFixed(2)}%<br />₹ {totals.totalSGST.toFixed(2)}
                </td>
                <td className="border p-1 text-right font-semibold">₹ {totals.totalAmount.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>

          {/* TOTAL SECTION BELOW TABLE */}
          <div className="mt-2 text-right text-[12px]">
            <p>Sub Total: <span className="font-semibold">₹ {totals.subTotal.toFixed(2)}</span></p>
            <p>CGST {cgstRate.toFixed(2)}%: ₹ {totals.totalCGST.toFixed(2)}</p>
            <p>SGST {sgstRate.toFixed(2)}%: ₹ {totals.totalSGST.toFixed(2)}</p>
            <p className="text-gray-600">Total GST ({totals.gstPercent.toFixed(2)}%): ₹ {(totals.totalCGST + totals.totalSGST).toFixed(2)}</p>

            {orderData.discount > 0 && (
              <p className="text-red-600">Discount: -₹ {orderData.discount.toFixed(2)}</p>
            )}

            <p className="font-bold mt-1">Total: ₹ {totals.totalAmount.toFixed(2)}</p>
            <p>Payment Made: ₹ {paymentMade.toFixed(2)}</p>
            <p className="font-bold text-blue-600 mt-1">Balance: ₹ {Math.abs(totals.balanceDue).toFixed(2)}</p>
            <div className='flex item-center justify-between'>
              <div className="font-bold text-[11px] mb-1 text-gray-800">Amount in Words</div>
              <div className="text-[10px] italic bg-gray-50 p-1 rounded">
                {numberToWords(totals.totalAmount)}
              </div>
            </div>

          </div>

          {/* PAYMENT METHOD NOTICE */}
          <div className="border border-gray-300 p-2 mt-3 text-[11px] flex items-center justify-between gap-2">
            <p>
              <span className="font-bold">Payment Method:</span>{' '}
              <span className="font-semibold text-blue-800">{getPaymentType(orderData?.payment_type)}</span>
            </p>
            {isCod && (
              <p className="font-bold text-red-600">This order is COD</p>
            )}
          </div>

          {/* BANK DETAILS + SIGNATURE */}
          <div className="grid grid-cols-2 gap-3 mt-3">

            {/* Payment details based on payment method */}
            <div className="border border-gray-300 p-2 text-[11px]">
              {isCod ? (
                <>
                  <p className="font-bold mb-1">Cash on Delivery (COD)</p>
                  <p className="mb-1">
                    This order is Cash on Delivery. The remaining amount will
                    be collected at the time of delivery.
                  </p>
                  <p className="font-bold mb-1 mt-2">For any query, contact</p>
                  {companyContact}
                </>
              ) : (
                <>
                  <p className="font-bold mb-1">Company's Bank Details</p>
                  {companyBank}
                  {isQr && (
                    <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-200">
                      <img src={process.env.PUBLIC_URL + "/phonepayQr.jpg"} alt="QR Code" className="w-[70px] h-[70px] object-contain" />
                      <p className="text-[10px]">Scan to pay remaining amount via UPI / QR Code</p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Signature */}
            <div className="border border-gray-300 p-2 text-center flex flex-col justify-end">
              <div className="mt-8">
                <p className="font-semibold">Authorized Signature</p>
              </div>
            </div>
          </div>

          {/* TERMS & CONDITIONS */}
          <div className="mt-4 text-[10px] leading-4">
            <p className="font-semibold">Terms & Conditions</p>
            <p>1. Goods once sold will not be taken back.</p>
            <p>2. Warranty excluded for unauthorized repairs.</p>
            <p>3. Buyer will be personally liable to pay cheque bounce charges.</p>
          </div>

          {/* FOOTER */}
          <div className="text-center text-[10px] mt-3 border-t pt-2">
            www.equipmedy.com
          </div>

        </div>
      </div>
    </div>
  );
};

export default Invoice;
